import { useEffect, useMemo, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../ui/empty";
import { Input } from "../ui/input";
import { Separator } from "../ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { searchEverything, type SearchResults } from "../../lib/search";
import { formatTitleCase } from "../../lib/formatting";

type GlobalSearchProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (route: string) => void;
};

const emptyResults: SearchResults = { threads: [], entities: [], customers: [] };
const semanticFeatureEnabled =
  (import.meta.env.VITE_SEMANTIC_SEARCH_ENABLED ?? "false").toLowerCase() === "true";

function isTypingTarget(target: EventTarget | null) {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.isContentEditable
  );
}

export function GlobalSearch({ open, onOpenChange, onNavigate }: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults>(emptyResults);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalResults = useMemo(
    () =>
      results.threads.length + results.entities.length + results.customers.length,
    [results]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) {
        return;
      }
      const key = event.key.toLowerCase();
      if ((event.metaKey || event.ctrlKey) && key === "k") {
        event.preventDefault();
        onOpenChange(true);
        return;
      }
      if (key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        onOpenChange(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults(emptyResults);
      setError(null);
      setIsLoading(false);
      return;
    }

    const trimmed = query.trim();
    if (!trimmed) {
      setResults(emptyResults);
      setError(null);
      setIsLoading(false);
      return;
    }

    let isActive = true;
    const handle = window.setTimeout(() => {
      setIsLoading(true);
      setError(null);
      searchEverything(trimmed, 6, {
        semantic: semanticFeatureEnabled,
      })
        .then((data) => {
          if (!isActive) {
            return;
          }
          setResults(data.results);
        })
        .catch((err) => {
          if (!isActive) {
            return;
          }
          const message =
            err instanceof Error ? err.message : "Search failed. Try again.";
          setError(message);
          setResults(emptyResults);
        })
        .finally(() => {
          if (isActive) {
            setIsLoading(false);
          }
        });
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(handle);
    };
  }, [open, query]);

  const handleNavigate = (route: string) => {
    onNavigate(route);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="top"
        className="border-border bg-background/95 p-6 backdrop-blur"
      >
        <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
          <SheetHeader className="text-left">
            <SheetTitle className="text-xl">Search everything</SheetTitle>
            <SheetDescription>
              Threads, work items, and customers are searchable from anywhere.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                aria-label="Search threads, work items, and customers"
                placeholder="Search threads, work items, customers..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="rounded-md border px-2 py-1 text-[11px] font-medium">
                ⌘K
              </span>
              <span className="rounded-md border px-2 py-1 text-[11px] font-medium">
                /
              </span>
            </div>
          </div>

          <Card className="border-border bg-card/70">
            <CardContent className="space-y-6 pt-6">
              {!query.trim() ? (
                <Empty className="border-none">
                  <EmptyHeader>
                    <EmptyTitle>Start typing to search</EmptyTitle>
                    <EmptyDescription>
                      We will group results by threads, work items, and customers.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : null}

              {isLoading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" role="status">
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Searching…
                </div>
              ) : null}

              {error ? (
                <Empty className="border-none" role="alert">
                  <EmptyHeader>
                    <EmptyTitle>Search failed</EmptyTitle>
                    <EmptyDescription>{error}</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : null}

              {!isLoading && !error && query.trim() && totalResults === 0 ? (
                <Empty className="border-none">
                  <EmptyHeader>
                    <EmptyTitle>No results found</EmptyTitle>
                    <EmptyDescription>
                      Try a different keyword or search by customer email.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              ) : null}

              {!isLoading && !error && totalResults > 0 ? (
                <>
                  <section className="space-y-3">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>Threads</span>
                      <Badge variant="secondary">{results.threads.length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {results.threads.map((thread) => (
                        <button
                          key={thread.id}
                          type="button"
                          onClick={() => handleNavigate(`/threads/${thread.id}`)}
                          className="group w-full rounded-lg border border-border/60 bg-background/60 p-3 text-left transition hover:border-border hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold">
                              {thread.subject ?? "Untitled conversation"}
                            </span>
                            <Badge variant="outline">
                              {formatTitleCase(thread.status)}
                            </Badge>
                            <Badge variant="secondary">
                              {formatTitleCase(thread.priority)}
                            </Badge>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {thread.summary}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>
                              {thread.customer?.name ??
                                thread.customer?.email ??
                                "No customer linked"}
                            </span>
                            <span>Updated {new Date(thread.updatedAt).toLocaleString()}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>

                  <Separator />

                  <section className="space-y-3">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>Work items</span>
                      <Badge variant="secondary">{results.entities.length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {results.entities.map((entity) => (
                        <button
                          key={entity.id}
                          type="button"
                          onClick={() => handleNavigate(`/entities/${entity.id}`)}
                          className="group w-full rounded-lg border border-border/60 bg-background/60 p-3 text-left transition hover:border-border hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold">
                              {entity.title}
                            </span>
                            <Badge variant="outline">
                              {formatTitleCase(entity.type)}
                            </Badge>
                            <Badge variant="secondary">
                              {formatTitleCase(entity.status)}
                            </Badge>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {entity.description}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatTitleCase(entity.priority)} priority</span>
                            <span>Updated {new Date(entity.updatedAt).toLocaleString()}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>

                  <Separator />

                  <section className="space-y-3">
                    <div className="flex items-center justify-between text-sm font-semibold">
                      <span>Customers</span>
                      <Badge variant="secondary">{results.customers.length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {results.customers.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => handleNavigate(`/customers/${customer.id}`)}
                          className="group w-full rounded-lg border border-border/60 bg-background/60 p-3 text-left transition hover:border-border hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold">
                              {customer.name ?? "Unnamed customer"}
                            </span>
                            {customer.email ? (
                              <Badge variant="outline">{customer.email}</Badge>
                            ) : null}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Updated {new Date(customer.updatedAt).toLocaleString()}
                          </p>
                        </button>
                      ))}
                    </div>
                  </section>
                </>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
}
